import {DocumentNode, gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import ModelTypeConstants, {
  STORABLE_ITEM_TYPES,
  STORABLE_TO_SCHEMA,
  StorableItemType,
} from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {AllStorableSchemasQuery} from 'registry/graphql/__generated/AllStorableSchemasQuery';
import {ALL_STORABLE_SCHEMAS_QUERY} from 'registry/graphql/AllStorableSchemas';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
  PlateSchemaType,
  StorableSchema,
} from 'samples/storables/StorableSchemaConstants';
import {isSchemaTypeFilterOption, Schema, SchemaTypeFilterOption} from 'schemas/SchemaConstants';
import {SchemaOwnerQuery} from 'schemas/SchemaOwnerUtil';
import {getSchemaSelectorPlaceholder} from 'schemas/shared/getSchemaSelectorPlaceholder';
import APIUtil from 'util/APIUtil';

import {AllStorableSchemaSelectorOwnerQuery} from './__generated/AllStorableSchemaSelectorOwnerQuery';
import {Section} from './SchemaStarsUtil';
import StorableSchemaSelector from './StorableSchemaSelector';
import {
  getValidBoxSchemasFromQuery,
  getValidContainerSchemasFromQuery,
  getValidLocationSchemasFromQuery,
  getValidPlateSchemasFromQuery,
} from './StorablesSchemaSelectorUtils';

const storableToTitle = new Map<string, string>([
  [ModelTypeConstants.CONTAINER, 'Container Schemas'],
  [ModelTypeConstants.BOX, 'Box Schemas'],
  [ModelTypeConstants.LOCATION, 'Location Schemas'],
  [ModelTypeConstants.PLATE, 'Plate Schemas'],
]);

function getSchemasFromQuery(
  storable: string,
  data: AllStorableSchemasQuery,
  registryId: string | undefined,
  plateSchemaType?: PlateSchemaType
): Array<StorableSchema> {
  switch (storable) {
    case ModelTypeConstants.CONTAINER:
      return getValidContainerSchemasFromQuery(data, registryId);
    case ModelTypeConstants.BOX:
      return getValidBoxSchemasFromQuery(data, registryId);
    case ModelTypeConstants.LOCATION:
      return getValidLocationSchemasFromQuery(data, registryId);
    default:
      return getValidPlateSchemasFromQuery(data, registryId, plateSchemaType);
  }
}

interface BaseAllStorablesSelectorProps {
  error?: string | null;
  isDisabled?: boolean;
  registryId?: string;
  shouldShowNoSchemaOption: boolean;
  /** If true, enables an any/all option for each storable type. For single select, this takes the form of an
   * "any" option under each section, e.g. "Any plate" under the plates section. Just the one filter option
   * can be selected and must be expanded or otherwise handled by the caller.
   * For multi select cases, this means an "All" option at the very top (outside the type sections). Selecting
   * this option auto-selects all schemas of that type. If any specific schemas of that type are de-selected,
   * the all option is de-selected as well.
   * If this is passed as true, the on change handler must handle receiving these filter options that are not
   * valid schemas.
   */
  shouldShowAnySchemaOfTypeOptions?: boolean;
  // If includeStorableTypes is not specified, all storable types will be included
  // If includeStorableTypes is specified, only storable types in the array will be included in the selector
  includeStorableTypes?: Array<StorableItemType>;
  // If plateSchemaType is specified, only plates of that type will be included
  plateSchemaType?: PlateSchemaType;
  // if saveStorableSchemas is specified, fetched schemas can be preserved for the parent component
  saveStorableSchemas?: (schemas: Array<StorableSchema>) => void;
  isMulti?: boolean;
}

export interface AllStorablesSelectorProps extends BaseAllStorablesSelectorProps {
  onChange: (
    schemaOrFilterOption: ContainerSchema | BoxSchema | LocationSchema | PlateSchema | SchemaTypeFilterOption
  ) => void;
  selectedAllStorablesSchemaId: string | null;
  isMulti?: false;
}

export interface MultiSelectAllStorablesSelectorProps extends BaseAllStorablesSelectorProps {
  onChangeSelectedSchemaIds: (selectedSchemaIds: Array<string>) => void;
  selectedSchemaIds: Array<string>;
  isMulti: true;
  // No schema option not currently supported for multi-select
  shouldShowNoSchemaOption: false;
}

export const ALL_STORABLE_SCHEMA_SELECTOR_OWNER_QUERY: DocumentNode = gql`
  query AllStorableSchemaSelectorOwnerQuery @skipAudit @engComponent(name: INVENTORY) {
    plateSchemas {
      ...StorableSchemaOwner
    }
    boxSchemas {
      ...StorableSchemaOwner
    }
    locationSchemas {
      ...StorableSchemaOwner
    }
    containerSchemas {
      ...StorableSchemaOwner
    }
  }
  fragment StorableSchemaOwner on StorableSchemaInterface {
    id
    registry {
      id
      organization {
        id
        name
      }
    }
  }
`;

function AllStorablesSchemaSelector(
  // Distinct props exposed for single & multi-select versions to align with different components used downstream
  {
    shouldShowNoSchemaOption = true,
    ...props
  }: AllStorablesSelectorProps | MultiSelectAllStorablesSelectorProps
): JSX.Element | null {
  const result = useBenchlingQuery<AllStorableSchemasQuery>(ALL_STORABLE_SCHEMAS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const {includeStorableTypes, shouldShowAnySchemaOfTypeOptions} = props;
  const {
    storableTypes,
    allowedAnyOfSchemaTypes,
  }: {
    storableTypes: Array<StorableItemType>;
    allowedAnyOfSchemaTypes: Array<ModelTypeConstants>;
  } = useMemo(() => {
    const storableTypes = includeStorableTypes ?? ([...STORABLE_ITEM_TYPES] as Array<StorableItemType>);
    const allowedAnyOfSchemaTypes = shouldShowAnySchemaOfTypeOptions
      ? storableTypes.map((storableType) => STORABLE_TO_SCHEMA[storableType]!)
      : [];
    return {storableTypes, allowedAnyOfSchemaTypes};
  }, [includeStorableTypes, shouldShowAnySchemaOfTypeOptions]);

  if (result.type === QueryResultType.ERROR) {
    return null;
  }
  let allStorablesSchemas: Array<StorableSchema | SchemaTypeFilterOption> | null;
  const schemaTypeToSchemaIds: {[schemaType: string]: Array<string>} = {};
  const sectionArr: Array<Section> = [];
  if (result.type === QueryResultType.PENDING) {
    allStorablesSchemas = null;
  } else {
    allStorablesSchemas = [];
    // Render all of the "All <storable type> schemas" options in their own section at the top for the multi-
    // select case to more clearly delineate them. For single select, they can just be inline in their
    // respective sections
    if (props.shouldShowAnySchemaOfTypeOptions && props.isMulti) {
      sectionArr.push({
        containsSchema: (schema: Schema | SchemaTypeFilterOption): boolean =>
          isSchemaTypeFilterOption(schema),
      });
    }
    for (const storable of storableTypes) {
      sectionArr.push({
        containsSchema: (schema: Schema | SchemaTypeFilterOption): boolean =>
          (!props.isMulti &&
            isSchemaTypeFilterOption(schema) &&
            schema.schemaType === STORABLE_TO_SCHEMA[storable]) ||
          APIUtil.apiIdToMaybeType(schema.id) === STORABLE_TO_SCHEMA[storable],
        title: storableToTitle.get(storable) as string,
      });

      const schemaType = STORABLE_TO_SCHEMA[storable]!;
      if (props.shouldShowAnySchemaOfTypeOptions) {
        allStorablesSchemas.push({
          id: `any_${schemaType}`,
          name: props.isMulti ? `All ${storable} schemas` : `Any ${storable}`,
          schemaType,
          isSchemaTypeFilterOption: true,
        });
      }
      const schemasOfType = getSchemasFromQuery(
        storable,
        result.data,
        props.registryId,
        props.plateSchemaType
      );
      allStorablesSchemas.push(...schemasOfType);
      schemaTypeToSchemaIds[schemaType] = schemasOfType.map(({id}) => id);
    }
    const allValidStorableSchemas = allStorablesSchemas.flatMap((schemaOrFilterOption) =>
      !isSchemaTypeFilterOption(schemaOrFilterOption) ? [schemaOrFilterOption] : []
    );
    if (props.saveStorableSchemas && allValidStorableSchemas.length) {
      props.saveStorableSchemas(allValidStorableSchemas);
    }
  }

  return (
    <SchemaOwnerQuery<AllStorableSchemaSelectorOwnerQuery>
      query={ALL_STORABLE_SCHEMA_SELECTOR_OWNER_QUERY}
      getIdOwnerTuples={(data) =>
        [...data.plateSchemas, ...data.boxSchemas, ...data.locationSchemas, ...data.containerSchemas].map(
          (schema) => ({id: schema.id, owner: schema.registry.organization.name})
        )
      }
    >
      {({getSchemaOwner}) => {
        const sharedProps = {
          starredSchemaIds: [],
          onToggleStar: noop,
          sections: sectionArr,
          getSchemaOwner,
          schemas: allStorablesSchemas,
          error: props.error,
          isMulti: props.isMulti,
          isDisabled: Boolean(props.isDisabled),
        };

        if (props.isMulti) {
          const handleChangeSelectedSchemaIds = (selectedIds: Array<string>): void => {
            if (!props.shouldShowAnySchemaOfTypeOptions) {
              props.onChangeSelectedSchemaIds(selectedIds);
              return;
            }
            let newSchemaIds = selectedIds;
            // Handle expanding "any" option out to all schemas of that type. Necessary because the isMulti
            // version stays open and shows all selected items.
            for (const anyOfSchemaType of allowedAnyOfSchemaTypes) {
              const anyOptionId = `any_${anyOfSchemaType}`;
              const anyOptionSelected = selectedIds.includes(anyOptionId);
              const schemasOfType = schemaTypeToSchemaIds[anyOfSchemaType];
              if (anyOptionSelected !== props.selectedSchemaIds.includes(anyOptionId)) {
                // If "any" option is newly selected or deselected (incoming props do not match current props),
                // handle by adding or removing all schema ids of the appropriate type
                if (anyOptionSelected) {
                  // "Any" option is now selected but was not previously: add all schemas of that type
                  newSchemaIds = [...new Set([...newSchemaIds, ...schemasOfType])];
                } else {
                  // "Any" option was deselected: remove all schemas of that type
                  newSchemaIds = newSchemaIds.filter((schemaId) => !schemasOfType.includes(schemaId));
                }
              } else if (
                anyOptionSelected &&
                schemasOfType.some((schemaId) => !selectedIds.includes(schemaId))
              ) {
                // "Any" option was previously selected, but some items of that type were deselected: also
                // deselect the "Any" option
                newSchemaIds = newSchemaIds.filter((schemaId) => schemaId !== anyOptionId);
              }
            }
            props.onChangeSelectedSchemaIds(newSchemaIds);
          };
          return (
            <StorableSchemaSelector
              {...sharedProps}
              isMulti={true}
              selectedSchemaIds={props.selectedSchemaIds}
              onSelectSchemaIds={handleChangeSelectedSchemaIds}
            />
          );
        }
        return (
          <StorableSchemaSelector
            {...sharedProps}
            isMulti={false}
            onChange={props.onChange}
            selectedSchemaId={props.selectedAllStorablesSchemaId}
            shouldShowNoSchemaOption={shouldShowNoSchemaOption}
            placeholder={getSchemaSelectorPlaceholder(result.type)}
          />
        );
      }}
    </SchemaOwnerQuery>
  );
}

export default AllStorablesSchemaSelector;
